import { useCallback, useEffect, useMemo, useState } from 'react'
import find from 'lodash/find'
import { useFormikContext } from 'formik'
import ClassNames from 'classnames'

import { config } from '../../Init/Init'
import { useCryptoPaymentDataModal } from '../CryptoPaymentModalDataProvider'
import { CryptoPaymentDepositInfo } from './CryptoPaymentDepositInfo'
import { CryptoPaymentDepositAddress } from './CryptoPaymentDepositAddress'
import { useGetCryptoChannel, useGetCryptoCurrencyExchangeRate, useOnCoinsAmountChange } from './hooks'
import { depositFormFields } from '../cryptoPaymentConstants'

import classes from './styles/cryptoPaymentDeposit.module.scss'
import { ReactComponent as CrossArrows } from '../../../static/images/icons/cross-arrows.svg'

export const CryptoPaymentDepositFormContent = () => {
  const [protocol, setProtocol] = useState(null)
  const [cryptoDepositError, setCryptoDepositError] = useState(null)

  const { loading, setLoading } = useCryptoPaymentDataModal()
  const {
    cryptoCurrencies,
    setSelectedCurrency,
    isCryptoCurrenciesLoading,
    lastPurchasedCryptoCurrencyData,
    isGetLastPurchasedCryptoCurrencyLoading
  } = useCryptoPaymentDataModal()

  const { setFieldValue, values } = useFormikContext()
  const { exchangeRate, isExchangeRateLoading, getCryptoCurrencyExchangeRate } = useGetCryptoCurrencyExchangeRate({
    setCryptoDepositError
  })
  const { getCryptoChannel, cryptoChannelError, cryptoChannelData } = useGetCryptoChannel({
    setLoading,
    setCryptoDepositError,
    getCryptoCurrencyExchangeRate
  })
  const { onCoinsAmountChange, cryptoCurrencyConfigs, updatedSectionName } = useOnCoinsAmountChange(exchangeRate)

  const getDepositAddress = useCallback(() => {
    let cryptoAddress = cryptoChannelData.depositAddress

    if (protocol) {
      const alternative = find(cryptoChannelData.alternatives, { protocol: protocol })

      if (alternative && alternative.address) {
        cryptoAddress = alternative.address
      }
    }

    return cryptoAddress
  }, [cryptoChannelData.alternatives, cryptoChannelData.depositAddress, protocol])

  const onCurrencyChange = useCallback(
    async currency => {
      await getCryptoChannel(currency)

      setSelectedCurrency(currency)
      setProtocol(null)
    },
    [setSelectedCurrency, getCryptoChannel]
  )

  useEffect(() => {
    if (cryptoCurrencies.length !== 0 && !isGetLastPurchasedCryptoCurrencyLoading) {
      // set previously selected currency (crypto) on init
      if (lastPurchasedCryptoCurrencyData.completedPurchasesExist) {
        const cryptoCurrency = cryptoCurrencies.find(
          cryptoCurrency => cryptoCurrency.currencyCode === lastPurchasedCryptoCurrencyData.cryptoCurrency
        )

        setFieldValue(depositFormFields.cryptoCurrencyCode, cryptoCurrency.currencyCode)
        onCurrencyChange(cryptoCurrency)

        // set default currency (crypto) on init
      } else if (!values[depositFormFields.cryptoCurrencyCode]) {
        setFieldValue(depositFormFields.cryptoCurrencyCode, cryptoCurrencies[0].currencyCode)
        onCurrencyChange(cryptoCurrencies[0])
      }
    }
  }, [cryptoCurrencies, isGetLastPurchasedCryptoCurrencyLoading])

  useEffect(() => {
    setFieldValue(depositFormFields.destinationTag, cryptoChannelData.destinationTag)
    setFieldValue(depositFormFields.address, getDepositAddress())
  }, [cryptoChannelData])

  const isDataLoading = useMemo(() => loading || isCryptoCurrenciesLoading || isExchangeRateLoading, [
    loading,
    isExchangeRateLoading,
    isCryptoCurrenciesLoading
  ])

  const crossArrowsClassName = ClassNames(classes.cryptoPaymentDeposit__crossArrowsIcon, {
    [classes.cryptoPaymentDeposit__crossArrowsIcon_active]: updatedSectionName
  })

  return (
    <>
      <CryptoPaymentDepositInfo onCoinsAmountChange={onCoinsAmountChange} updatedSectionName={updatedSectionName} />
      <div className={classes.cryptoPaymentDeposit__crossArrows}>
        <CrossArrows className={crossArrowsClassName} />
      </div>
      {!config.useBvnkHostedPage && (
        <CryptoPaymentDepositAddress
          loading={loading}
          protocol={protocol}
          setProtocol={setProtocol}
          isDataLoading={isDataLoading}
          onCurrencyChange={onCurrencyChange}
          getDepositAddress={getDepositAddress}
          cryptoChannelData={cryptoChannelData}
          cryptoChannelError={cryptoChannelError}
          updatedSectionName={updatedSectionName}
          onCoinsAmountChange={onCoinsAmountChange}
          cryptoCurrencyConfigs={cryptoCurrencyConfigs}
        />
      )}
      {cryptoDepositError && !loading && (
        <div className={classes.cryptoPaymentDeposit__error}>{cryptoDepositError}</div>
      )}
      {config.useBvnkHostedPage &&
        !isDataLoading &&
        !cryptoChannelError &&
        values[depositFormFields.cryptoCurrencyCode] && (
          <iframe
            className={classes.cryptoPaymentDeposit__iframe}
            src={cryptoChannelData.bvnkHostedPageUrl}
            id="crypto-payment-iframe"
          />
        )}
    </>
  )
}
